import { UndoOutlined } from '@ant-design/icons';
import { InputNumber, Tooltip, Typography } from 'antd';
import { useCallback } from 'react';
import styles from '../../Style/LegendScaleSlider.module.scss';

interface IMinMax {
  legendMinValue: number;
  legendMaxValue: number;
}

interface LegendScaleSliderProps {
  showLegendScale: boolean;
  startValue: number;
  endValue: number;
  setStartValue: (val: number) => void;
  setEndValue: (val: number) => void;
  initialLegendValue: IMinMax;
  onLegendStartEndChanged: (min: number, max: number) => void;
  setIsEditing: (val: boolean) => void;
}

const LEGEND_MIN_VALUE = -10;
const LEGEND_MAX_VALUE = 9999;

const LegendScaleSlider = ({
  showLegendScale,
  setStartValue,
  setEndValue,
  startValue,
  endValue,
  setIsEditing,
  initialLegendValue,
  onLegendStartEndChanged,
}: LegendScaleSliderProps) => {
  const handleLegendStartChange = useCallback(
    (value: number | null) => {
      if (value === null) return;

      setIsEditing(true);
      const finalValue = Number(value.toFixed(1));
      const clampedValue = Math.max(
        LEGEND_MIN_VALUE,
        Math.min(LEGEND_MAX_VALUE, finalValue)
      );
      const actualClampedValue = Math.min(clampedValue, endValue);

      setStartValue(actualClampedValue);
      onLegendStartEndChanged(actualClampedValue, endValue);
    },
    [endValue]
  );

  const handleLegendEndChange = useCallback(
    (value: number | null) => {
      if (value === null) return;

      setIsEditing(true);
      const finalValue = Number(value.toFixed(1));
      const clampedValue = Math.max(
        LEGEND_MIN_VALUE,
        Math.min(LEGEND_MAX_VALUE, finalValue)
      );
      const actualClampedValue = Math.max(clampedValue, startValue);

      setEndValue(actualClampedValue);
      onLegendStartEndChanged(startValue, actualClampedValue);
    },
    [startValue]
  );

  const handleClearLegend = useCallback(() => {
    const resetStartValue = Math.max(
      LEGEND_MIN_VALUE,
      initialLegendValue.legendMinValue
    );
    const resetEndValue = Math.max(
      LEGEND_MIN_VALUE,
      initialLegendValue.legendMaxValue
    );

    setIsEditing(false);
    setStartValue(resetStartValue);
    setEndValue(resetEndValue);
    onLegendStartEndChanged(resetStartValue, resetEndValue);
  }, [initialLegendValue, setStartValue, setEndValue, onLegendStartEndChanged]);

  if (!showLegendScale) return null;

  return (
    <div className={styles.container}>
      <div className={styles.inputNumberContainer}>
        <div>
          <Tooltip title='Reset Legend Interval'>
            <UndoOutlined
              style={{ color: 'black' }}
              onClick={handleClearLegend}
            />
          </Tooltip>
        </div>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>Min</Typography>
          <InputNumber
            value={Math.max(LEGEND_MIN_VALUE, startValue)}
            onChange={handleLegendStartChange}
            min={LEGEND_MIN_VALUE}
            max={LEGEND_MAX_VALUE}
            step={0.1}
            precision={1}
            style={{ width: 100 }}
          />
        </div>
        <div className={styles.inputWrapper}>
          <Typography style={{ marginRight: 5, width: 40 }}>Max</Typography>
          <InputNumber
            value={endValue}
            onChange={handleLegendEndChange}
            min={LEGEND_MIN_VALUE}
            max={LEGEND_MAX_VALUE}
            step={0.1}
            precision={1}
            style={{ width: 100 }}
          />
        </div>
      </div>
    </div>
  );
};

export default LegendScaleSlider;
